import Button from '../Button'
import ToggleSwitch3 from '../ToggleSwitch3'
import DropDown2 from '../DropDown2'
import MultiSelectDropdown from '../MultiSelectDropdown'
import HelixSearchPersonFilterTag from './HelixSearchPersonFilterTag'
import { formatAbbreviatedNumber } from '../../utils/numberUtils'
import { PersonLiteDto } from '../../api/person/models/PersonDto'
import { MapExtra } from '../../utils/MapExtra'
import LoadingSpinner from '../LoadingSpinner'
import Note from '../Note'
import { NoteType } from '../../enums/NoteType'

interface Props {
    roleId: string | null
    personLiteCache: MapExtra<string, PersonLiteDto>
    availableNetworkLinkIds: string[]
    applyClick: () => void
    resetClick: () => void
    networkLinksSelected: string[]
    shortlistedSelected: string[]
    minRelationshipScore: string
    orderBy: string
    mustBeInBooleanSearchResults: boolean
    mustHaveRelationship: boolean
    filteredCandidatePoolCount: number
    filteredCandidatePoolCountIsLoading: boolean
    filtersAreDirty: boolean
    networkLinksSelectedChanged: (selectedIds: string[]) => void
    shortlistedSelectedChanged: (selectedIds: string[]) => void
    minRelationshipScoreChanged: (selectedId: string) => void
    orderByChanged: (selectedId: string) => void
    mustBeInBooleanSearchResultsChanged: (value: boolean) => void
    mustHaveRelationshipChanged: (value: boolean) => void
}

// TODO: 77 - when filters change we should call /get-helix-search-results-count-only to update the candidate pool count
// TODO: 77 - hide the shortlist filter if we're not in the context of a role
// TODO: 77 - need an "isDirty" flag to enable/disable the apply button

export default function HelixSearchFilters({
    roleId,
    personLiteCache,
    availableNetworkLinkIds,
    applyClick,
    resetClick,
    networkLinksSelected,
    shortlistedSelected,
    minRelationshipScore,
    orderBy,
    mustBeInBooleanSearchResults,
    mustHaveRelationship,
    filteredCandidatePoolCount,
    filteredCandidatePoolCountIsLoading,
    filtersAreDirty,
    networkLinksSelectedChanged,
    shortlistedSelectedChanged,
    minRelationshipScoreChanged,
    orderByChanged,
    mustBeInBooleanSearchResultsChanged,
    mustHaveRelationshipChanged,
}: Props) {
    return (
        <div>
            <div className="mt-4">
                <div>
                    <MultiSelectDropdown
                        label="Network Links"
                        onSelectionChange={networkLinksSelectedChanged}
                        selectedItemIds={networkLinksSelected}
                        items={availableNetworkLinkIds
                            .slice() // make a shallow copy to avoid mutating original array
                            .sort((a, b) => {
                                const personA = personLiteCache.getOrThrow(a)
                                const personB = personLiteCache.getOrThrow(b)

                                const fullNameA =
                                    `${personA.firstName} ${personA.lastName}`.toLowerCase()
                                const fullNameB =
                                    `${personB.firstName} ${personB.lastName}`.toLowerCase()

                                return fullNameA.localeCompare(fullNameB)
                            })
                            .map((personId) => {
                                const person =
                                    personLiteCache.getOrThrow(personId)

                                return {
                                    id: person.id,
                                    value: (
                                        <HelixSearchPersonFilterTag
                                            personLite={person}
                                        />
                                    ),
                                }
                            })}
                    />
                </div>
                <div className="flex gap-4 mt-4 items-center">
                    {roleId && (
                        <MultiSelectDropdown
                            label="Shortlisted"
                            onSelectionChange={shortlistedSelectedChanged}
                            selectedItemIds={shortlistedSelected}
                            items={[
                                {
                                    id: 'blanks',
                                    value: <div className="italic">blanks</div>,
                                },
                                { id: 'yes', value: 'Yes' },
                                { id: 'no', value: 'No' },
                            ]}
                        />
                    )}
                    <DropDown2
                        label="Minimum Network Strength"
                        items={[
                            {
                                id: 'min_relationship_score_any',
                                value: 'Any',
                            },
                            {
                                id: 'min_relationship_score_1',
                                value: '1',
                            },
                            {
                                id: 'min_relationship_score_10',
                                value: '10',
                            },
                            {
                                id: 'min_relationship_score_20',
                                value: '20',
                            },
                            {
                                id: 'min_relationship_score_30',
                                value: '30',
                            },
                            {
                                id: 'min_relationship_score_40',
                                value: '40',
                            },
                            {
                                id: 'min_relationship_score_50',
                                value: '50',
                            },
                            {
                                id: 'min_relationship_score_60',
                                value: '60',
                            },
                            {
                                id: 'min_relationship_score_70',
                                value: '70',
                            },
                            {
                                id: 'min_relationship_score_80',
                                value: '80',
                            },
                            {
                                id: 'min_relationship_score_90',
                                value: '90',
                            },
                            {
                                id: 'min_relationship_score_100',
                                value: '100',
                            },
                        ]}
                        selectedItemId={minRelationshipScore}
                        onItemSelected={minRelationshipScoreChanged}
                    />
                    <DropDown2
                        label="Order By"
                        items={[
                            {
                                id: 'combined_desc',
                                value: 'Combined (highest first)',
                            },
                            {
                                id: 'combined_asc',
                                value: 'Combined (lowest first)',
                            },
                            {
                                id: 'booleanSearchScore_desc',
                                value: 'Job Relevance (highest first)',
                            },
                            {
                                id: 'booleanSearchScore_asc',
                                value: 'Job Relevance (lowest first)',
                            },
                            {
                                id: 'relationshipScore_desc',
                                value: 'Network Strength (highest first)',
                            },
                            {
                                id: 'relationshipScore_asc',
                                value: 'Network Strength (lowest first)',
                            },
                        ]}
                        selectedItemId={orderBy}
                        onItemSelected={orderByChanged}
                    />
                    <div className="grid gap-2 items-center">
                        <div className="grid grid-cols-[160px_auto] items-center">
                            <span className="text-sm text-gray-900">
                                Must match job criteria
                            </span>
                            <ToggleSwitch3
                                value={mustBeInBooleanSearchResults}
                                onChange={mustBeInBooleanSearchResultsChanged}
                            />
                        </div>
                        <div className="grid grid-cols-[160px_auto] items-center">
                            <span className="text-sm text-gray-900">
                                Must be in network
                            </span>
                            <ToggleSwitch3
                                value={mustHaveRelationship}
                                onChange={mustHaveRelationshipChanged}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-between mt-4">
                <div className="flex gap-2">
                    <Button
                        type="button"
                        style="primary"
                        onClick={applyClick}
                        size="small"
                        enabled={filtersAreDirty}
                    >
                        Apply Filters
                    </Button>
                    <Button
                        type="button"
                        style="mild-danger"
                        onClick={resetClick}
                        size="small"
                    >
                        Reset Filters
                    </Button>
                </div>
                <div className="text-right">
                    <div className="text-sm text-gray-900">
                        Filtered Candidate Pool
                    </div>
                    {filteredCandidatePoolCountIsLoading && (
                        <div className="text-right">
                            <LoadingSpinner spinnerSize="28px" />
                        </div>
                    )}
                    {!filteredCandidatePoolCountIsLoading && (
                        <div
                            className={`text-xl font-mono tracking-wide font-bold ${
                                filteredCandidatePoolCount > 0
                                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-ctxt-blue-dark via-ctxt-blue to-sky-500'
                                    : 'text-red-400'
                            }`}
                        >
                            {formatAbbreviatedNumber(
                                filteredCandidatePoolCount,
                            )}
                        </div>
                    )}
                </div>
            </div>
            {filtersAreDirty && (
                <Note type={NoteType.WARNING}>
                    Your filters have changed. Apply them to update results.
                </Note>
            )}
        </div>
    )
}
